import { useI18n } from "../../lib/i18n";
import { useEffect, useRef, useState } from "react";
import { useSearch } from "wouter";
import { Input } from "../beui/Input";
import { Button } from "../beui/Button";
import { api } from "../../lib/api";
import type { SupportRequest } from "../../lib/api-client/support";
import { ApiError } from "../../lib/api-client/transport";
import { supportAccessLost, supportError } from "./useSupportRead";
import { supportCategories } from "./SupportShared";
import { SupportImageSelection } from "./SupportImageSelection";

export function SupportRequestForm({ onCreated }: { onCreated: (id: string) => void }) {
  const { t } = useI18n();
  const search = useSearch();
  const withdrawal = new URLSearchParams(search).get("withdrawal")?.slice(0, 120);
  const [subject, setSubject] = useState(withdrawal ? "Help with my withdrawal" : "");
  const [category, setCategory] = useState(withdrawal ? "payment" : "bug");
  const [body, setBody] = useState(withdrawal ? `Please check my unresolved withdrawal. Reference: ${withdrawal}` : "");
  const [attempt, setAttempt] = useState<SupportRequest | null>(null);
  const [images, setImages] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const active = useRef(true);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  async function submit(event: React.FormEvent) {
    event.preventDefault(); if (busy) return;
    const command = attempt || { subject: subject.trim(), category, body: body.trim(), requestId: crypto.randomUUID(), images };
    setAttempt(command); setBusy(true); setError("");
    try { const result = await api.createTicket(command); if (active.current) onCreated(result.ticket.id); }
    catch (failure) {
      if (!active.current) return;
      if (supportAccessLost(failure)) { setSubject(""); setBody(""); setImages([]); setAttempt(null); }
      else if (failure instanceof ApiError && ([400, 413].includes(failure.status) || failure.code === "support_upload_failed")) setAttempt(null);
      setError(supportError(failure));
    } finally { if (active.current) setBusy(false); }
  }
  return <form className="support-panel support-form" onSubmit={submit} data-testid="support-form">
    <div><h2>{t("support.newRequest")}</h2><p className="text-sm text-mut mt-1">An Admin will reply here. Email notices depend on your settings.</p></div>
    <Input label="Subject" required minLength={3} maxLength={200} value={subject} onChange={setSubject} disabled={!!attempt} placeholder="Brief summary" />
    <div className="support-field"><label htmlFor="support-category">Category</label><select id="support-category" className="field" value={category} onChange={event => setCategory(event.target.value)} disabled={!!attempt}>{supportCategories.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div>
    <div className="support-field"><label htmlFor="support-details">Details</label><textarea id="support-details" className="field" rows={4} required minLength={3} maxLength={4000} value={body} onChange={event => setBody(event.target.value)} disabled={!!attempt} placeholder="What happened?" /></div>
    <SupportImageSelection files={images} onChange={setImages} disabled={busy || !!attempt} />
    {error && <div role="alert" className="text-sm text-brand-red"><p>{error}</p>{attempt && <p>Retry to confirm this submission before editing.</p>}</div>}
    <Button type="submit" className="support-primary" disabled={busy} data-testid="support-submit">{busy ? "Sending…" : "Submit request"}</Button>
  </form>;
}
